// ---------------------------------------------------------------------------
// W21 rung ② — the SINGLE definition of "a pasted intake document must not be
// treated as a change request".
//
// Why this module exists (2026-08-18 ASIS→TOBE journey census, table row #10):
// the intercept was written once, inside `chat-rail-input-bar.tsx`, and unit-
// verified there. But a project thread has TWO composers — the build-mode rail
// input bar, and `StudioComposer` (chat-panel.tsx → chat-drawer-slot.tsx),
// which the rail delegates to whenever `studioMode !== 'build'`. Only the
// first one ever consulted the intake classifier.
//
// Measured at this branch's base with the real customer fixture
// (`e2e/fixtures/legacy-screen-loan-limit.jsp`):
//   routeStudioCreationRequest(fixture).kind    === 'legacy-screen-readback'
//   shouldRouteAsAppCreation(fixture, /*open*/ true)  === false
//   shouldRouteAsAppCreation(fixture, /*open*/ false) === false
// StudioComposer only calls `routeStudioCreationRequest` INSIDE its
// `if (appCreationPrompt)` branch — which a pasted legacy screen can never
// enter, because a screen source contains no creation-shaped language. So the
// paste fell through to `submitGroundedStudioIntent` (the element-edit lane)
// and came back as the `ambiguous-target` refusal "변경할 부분을 정확히
// 선택하세요" — an answer about selecting an element, to a user who had just
// handed the product a document. Mid-journey document layering (S4 → S1/S2)
// was therefore impossible from that composer.
//
// One definition, two call sites: the routing decision, the neutral carry, the
// toast, and the navigation all live HERE so the two composers cannot answer a
// pasted document differently again.
//
// SCOPE (honest limit, deliberately not widened here): the carry lands the
// document in the HOME composer, where the S1–S4/S7 read-back cards live, and
// the current project is closed. That is the product's existing designed
// destination for a document, and it is what makes the path reachable and the
// response actionable. It is NOT in-thread layering onto the already-open
// project — no surface in the IDE shell hosts an intake card today, so that
// remains open work (census R1's second half).
// ---------------------------------------------------------------------------

import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'
import { useStudioStore } from '@/store/studio-store'
import { showStudioRuntimeToast } from '@/shell/ide-v2/host/studio-runtime-toast'
import { routeStudioCreationRequest } from './studio-creation-router'
import { writeIntakeCarry } from './intake-carry'

/**
 * The one notice both composers show. Copied verbatim from the rail's original
 * per-locale strings so this consolidation changes no wording, only ownership.
 */
export const INTAKE_DOCUMENT_CARRY_NOTICE: Record<LocaleCode, string> = {
  en: 'Document recognized — opening the read-back in the home composer.',
  ko: '문서를 인식했습니다 — 홈 컴포저에서 읽어보기를 진행합니다.',
  ja: 'ドキュメントを認識しました — ホームで読み取りを開始します。',
  zh: '已识别文档 — 将在主页进行读取确认。',
  'zh-TW': '已識別文件 — 將在主頁進行讀取確認。',
}

/**
 * Route a composer submission that turns out to be an intake DOCUMENT.
 *
 * Returns `true` when the submission was consumed (carried + navigated) and the
 * caller must stop; `false` when this is an ordinary prompt/edit, when an
 * element target is active (the user is editing a specific element, where
 * document routing has no business), or when the carry channel refused the
 * text — in which case the caller proceeds with its normal handling rather
 * than silently swallowing the input.
 */
export function routePastedIntakeDocument(input: {
  text: string
  locale: LocaleCode
  /** True when a refinement/element chip is active on the calling composer. */
  hasActiveElementTarget: boolean
}): boolean {
  if (input.hasActiveElementTarget) return false

  const creation = routeStudioCreationRequest(input.text)
  // Prose (`zero-base-generation`) and template matches are ordinary prompts.
  // Everything else the classifier names IS one of the intake documents whose
  // read-back cards gate generation.
  if (
    creation.kind === 'zero-base-generation' ||
    creation.kind === 'template-proposal'
  ) {
    return false
  }

  const state = useStudioStore.getState()
  const carried = writeIntakeCarry({
    principalId: state.user?.id ?? null,
    prompt: input.text,
  })
  if (!carried) return false

  showStudioRuntimeToast({ message: INTAKE_DOCUMENT_CARRY_NOTICE[input.locale] })
  if (state.currentProjectId) state.closeProject()
  window.history.pushState(null, '', '/studio')
  window.dispatchEvent(new PopStateEvent('popstate'))
  return true
}
